import * as React from 'react'
import type { CSSProperties } from 'react'
import { cn } from '../lib'
import { designTokens } from '../lib/design-tokens'
import { typographyStyle } from '../lib/catalog'
import { ChevronDownIcon } from './icons'
import type { SelectTokenStyle } from './select-variants'

const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, children, style, ...props }, ref) => {
    const styleEntries = Object.entries(style ?? {})
    const variableStyle = Object.fromEntries(
      styleEntries.filter(([key]) => key.startsWith('--select-'))
    ) as SelectTokenStyle
    const controlStyle = Object.fromEntries(
      styleEntries.filter(([key]) => !key.startsWith('--select-'))
    ) as CSSProperties
    const tokenVars: SelectTokenStyle = {
      '--select-bg': designTokens.colors.background.inputDefault,
      '--select-bg-disabled': designTokens.colors.background.inputDisabled,
      '--select-border': designTokens.colors.border.inputDefault,
      '--select-border-focus': designTokens.colors.border.inputFocus,
      '--select-border-disabled': designTokens.colors.border.inputDisabled,
      '--select-text': designTokens.colors.text.inputValue,
      '--select-text-disabled': designTokens.colors.text.inputDisabled,
      '--select-ring': designTokens.colors.border.inputFocus,
      '--select-ring-offset': designTokens.colors.background.inputDefault,
      '--select-icon': designTokens.colors.text.inputValue,
    }
    const horizontalPadding = designTokens.spacing.sm
    const chevronInset = designTokens.spacing.sm
    const tokenStyle: CSSProperties = {
      borderRadius: designTokens.borders.radius.sm,
      borderWidth: designTokens.borders.widths.thin,
      borderStyle: designTokens.borders.styles.solid,
      paddingTop: designTokens.spacing.xs,
      paddingBottom: designTokens.spacing.xs,
      paddingLeft: horizontalPadding,
      paddingRight: horizontalPadding,
      fontFamily: designTokens.typography.fontFamilies.primary,
      ...typographyStyle(designTokens.typography.input.text),
    }

    return (
      <div className="relative w-full" style={{ ...tokenVars, ...variableStyle }}>
        <select
          ref={ref}
          className={cn(
            'relative z-0 flex h-10 w-full appearance-none border border-[var(--select-border)] bg-[var(--select-bg)] text-[var(--select-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--select-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--select-ring-offset)] focus-visible:border-[var(--select-border-focus)] disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--select-bg-disabled)] disabled:text-[var(--select-text-disabled)] disabled:border-[var(--select-border-disabled)]',
            className
          )}
          style={{ ...tokenStyle, ...controlStyle }}
          {...props}
        >
          {children}
        </select>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 z-10 -translate-y-1/2"
          style={{ right: chevronInset, color: 'var(--select-icon)' }}
        >
          <ChevronDownIcon width={16} height={16} color="currentColor" />
        </span>
      </div>
    )
  }
)
Select.displayName = 'Select'

export { Select }
